<template>
  <div class="efficiency-calendar">
    <div class="calendar-header">
      <div class="weekday" v-for="day in weekdays" :key="day">{{ day }}</div>
    </div>
    <div class="calendar-grid">
      <div v-for="(day, index) in calendarDays" :key="index" class="calendar-day" :class="getDayClass(day)"
        :style="{ backgroundColor: getDayColor(day) }">
        <div class="day-content">
          <div class="day-number">{{ formatDate(day.date) }}</div>
          <div v-if="shouldShowValue(day)" class="day-value" :class="{ invalid: !isValidValue(day.value) }">{{
            isValidValue(day.value) ? (Number(day.value) + '%') : '--' }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import dayjs from 'dayjs'

const props = defineProps({
  data: {
    type: Object,
    default: () => ({
      month: null, // YYYY-MM 格式
      efficiencyData: {} // { 'YYYY-MM-DD': percentage }
    })
  }
})

const weekdays = ['二', '三', '四', '五', '六', '七', '一']

// 生成日历数据
const calendarDays = computed(() => {
  const month = props.data.month ? dayjs(props.data.month) : dayjs()
  const efficiencyData = props.data.efficiencyData || {}

  // 获取月份的第一天和最后一天
  const firstDay = month.startOf('month')
  const lastDay = month.endOf('month')

  const firstDayOfWeek = firstDay.day()
  const startOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1

  const days = []

  // 添加上个月末尾的几天
  if (startOffset > 0) {
    const prevMonth = month.subtract(1, 'month')
    const prevMonthLastDay = prevMonth.endOf('month').date()
    for (let i = startOffset - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i
      const date = prevMonth.date(day)
      const dateStr = date.format('YYYY-MM-DD')
      const value = Object.prototype.hasOwnProperty.call(efficiencyData, dateStr)
        ? efficiencyData[dateStr]
        : null

      days.push({
        day: day,
        date: dateStr,
        value: value,
        isCurrentMonth: false
      })
    }
  }

  // 添加当前月的所有天
  for (let day = 1; day <= lastDay.date(); day++) {
    const date = month.date(day)
    const dateStr = date.format('YYYY-MM-DD')
    const value = Object.prototype.hasOwnProperty.call(efficiencyData, dateStr)
      ? efficiencyData[dateStr]
      : null

    days.push({
      day: day,
      date: dateStr,
      value: value,
      isCurrentMonth: true
    })
  }

  // 添加下个月开头的几天（补齐到完整的周）
  const totalDays = days.length
  const remainingDays = 42 - totalDays // 6行 x 7列 = 42
  if (remainingDays > 0) {
    const nextMonth = month.add(1, 'month')
    for (let day = 1; day <= remainingDays; day++) {
      const date = nextMonth.date(day)
      const dateStr = date.format('YYYY-MM-DD')
      const value = Object.prototype.hasOwnProperty.call(efficiencyData, dateStr)
        ? efficiencyData[dateStr]
        : null

      days.push({
        day: day,
        date: dateStr,
        value: value,
        isCurrentMonth: false
      })
    }
  }

  return days
})

const isValidValue = (v) => {
  if (v === null || typeof v === 'undefined') return false
  const num = Number(v)
  return Number.isFinite(num) && num > 0 && num <= 100
}

// 避免未来日期显示 "--"
const shouldShowValue = (day) => {
  if (!day || !day.isCurrentMonth) return false
  const today = dayjs().startOf('day')
  const dayDate = dayjs(day.date)
  return !dayDate.isAfter(today)
}

const getDayColor = (day) => {
  if (!day) return 'transparent'
  const today = dayjs().startOf('day')
  const dayDate = dayjs(day.date)
  if (!day.isCurrentMonth || (day.isCurrentMonth && dayDate.isAfter(today))) return 'transparent'
  const value = day.value
  if (!isValidValue(value)) return 'rgba(255, 107, 107, 0.3)' // 红色：无数据或异常
  return 'rgba(98, 247, 172, 0.3)' // 绿色：0~100 (#62F7AC)
}

// 获取日期单元格的样式类
const getDayClass = (day) => {
  const today = dayjs().startOf('day')
  const dayDate = day && day.date ? dayjs(day.date) : null
  const isFutureInThisMonth = day && day.isCurrentMonth && dayDate && dayDate.isAfter(today)
  return {
    'not-current-month': !day.isCurrentMonth || isFutureInThisMonth,
    'has-value': isValidValue(day && day.value)
  }
}

// 格式化日期显示为 MM-DD
const formatDate = (dateStr) => {
  if (!dateStr) return ''
  return dayjs(dateStr).format('MM-DD')
}
</script>

<style scoped lang="scss">
.efficiency-calendar {
  width: 100%;
  height: 400px;
  min-height: 400px;
  padding: 8px;
  display: flex;
  flex-direction: column;
}

.calendar-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  margin-bottom: 2px;
  flex-shrink: 0;

  .weekday {
    text-align: center;
    font-size: 10px;
    font-family: 'HarmonyOS Sans SC';
    color: rgba(46, 46, 46, 0.70);
    font-weight: 300;
  }
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: 1fr;
  gap: 2px;
  flex: 1;
  min-height: 0;

  .calendar-day {
    border-radius: 4px;
    padding: 0;
    border: 1px solid rgba(106, 114, 124, 0.1);
    transition: all 0.2s;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 0;

    &::before {
      content: '';
      display: block;
      padding-bottom: 100%;
    }

    &.not-current-month {
      opacity: 0.3;
    }

    &.has-value {
      cursor: pointer;

      &:hover {
        transform: scale(1.05);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }
    }

    .day-content {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
    }

    .day-number {
      font-size: 9px;
      font-family: 'HarmonyOS Sans SC';
      font-weight: 400;
      color: #2E2E2E;
      margin-bottom: 1px;
      line-height: 1.2;
      text-align: center;
      white-space: nowrap;
    }

    .day-value {
      font-size: 9px;
      font-family: 'HarmonyOS Sans SC';
      font-weight: 500;
      color: #2E2E2E;
      line-height: 1.2;
      text-align: center;
      white-space: nowrap;
    }
  }
}

.day-value.invalid {
  color: #ff6b6b;
}
</style>
